import { useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  CloudFogIcon,
  CloudIcon,
  CloudLightningIcon,
  CloudMoonIcon,
  CloudRainIcon,
  CloudSnowIcon,
  CloudSunIcon,
  DropIcon,
  MoonIcon,
  MoonStarsIcon,
  SunHorizonIcon,
  SunIcon,
  WindIcon,
  type Icon,
} from '@phosphor-icons/react';
import SunCalc from 'suncalc';
import { apiFetch } from '../../../auth/apiFetch';
import { pollWithWake, useNow } from '../../../data/kioskRefresh';

// Kitchen kiosk weather widget. Reads /api/weather, which proxies Open-Meteo
// and returns current conditions, an hourly array spanning the past 24h
// through ~7 days forward, and a 7-day daily summary.
//
// Sized for a 1/3-width slot in the kiosk grid:
//   - Hero row: weather icon + current temp on the left, sunrise/sunset
//     chip vertically centered against the icon on the right
//   - 48-bar temperature chart for the current day (midnight → midnight,
//     30-min slots) with a "you are here" caret over the current slot
//     and time labels every 4 hours
//   - 4-day forecast strip across the bottom
//
// Subtle weather-condition accent (a radial wash from the top left) tints
// the card to match the current conditions (clear → amber, rain → blue,
// snow → icy sky, thunder → violet, cloud/fog → neutral), using existing
// design tokens.

interface CurrentReading {
  time: string;
  temperatureF: number;
  shortForecast: string;
  isDaytime: boolean;
}

interface HourlyPoint {
  time: string;
  temperatureF: number;
  shortForecast: string;
}

interface DailyPoint {
  date: string; // YYYY-MM-DD in the location's timezone
  highF: number;
  lowF: number;
  shortForecast: string;
}

interface WeatherResponse {
  current: CurrentReading;
  hourly: HourlyPoint[];
  daily: DailyPoint[];
  location: { lat: number; lon: number; timezone: string };
  updatedAt: string;
  stale?: boolean;
}

const REFRESH_MS = 10 * 60 * 1000;
// Horizontal pointer travel (px) that distinguishes a swipe from a stray
// click. Below this, the gesture is ignored so a casual tap doesn't
// accidentally page the carousel.
const SWIPE_THRESHOLD_PX = 40;
// Duration of the background condition glow's color transition on a page
// change. Matches the carousel's 300ms slide so the wash finishes morphing to
// the new preset's accent just as the slide settles.
const GLOW_FADE_MS = 300;
// After this much inactivity (no swipe), reset the carousel back to the
// first preset so a glance at the kiosk always lands on home. Timer is
// restarted on every interaction (even ones that don't move the index,
// like swiping past the last preset) so an actively-poking user never
// gets snapped back mid-look.
const INACTIVITY_RESET_MS = 15 * 60 * 1000;

// Presets for the swipeable weather carousel. Home stays first so the
// kiosk lands on Seattle on every mount. Lat/lon are exact enough for
// city-level forecasts (Open-Meteo grids are ~10km), timezones are IANA
// names passed through to the upstream API for correct local labeling.
interface WeatherPreset {
  name: string;
  lat: number;
  lon: number;
  timezone: string;
}

const PRESETS: WeatherPreset[] = [
  { name: 'Seattle', lat: 47.6062, lon: -122.3321, timezone: 'America/Los_Angeles' },
  { name: 'Syracuse', lat: 43.1431, lon: -76.1421, timezone: 'America/New_York' },
  { name: 'Lima', lat: -12.0464, lon: -77.0428, timezone: 'America/Lima' },
  { name: 'Spokane', lat: 47.6588, lon: -117.426, timezone: 'America/Los_Angeles' },
  { name: 'Wilbur', lat: 47.7574, lon: -118.7099, timezone: 'America/Los_Angeles' },
  { name: 'New York City', lat: 40.7128, lon: -74.006, timezone: 'America/New_York' },
];

function presetQuery(p: WeatherPreset): string {
  const params = new URLSearchParams({
    lat: String(p.lat),
    lon: String(p.lon),
    tz: p.timezone,
  });
  return `/api/weather?${params.toString()}`;
}

// Color stops for the hourly temperature bars — same indigo→red palette as
// the thermostat dial. Bar color is sampled by absolute temperature so cold
// hours read blue and warm hours read red, regardless of season.
const TEMP_COLOR_STOPS: Array<{ tempF: number; cssVar: string }> = [
  { tempF: 0, cssVar: '--berg-indigo' },
  { tempF: 100, cssVar: '--berg-red' },
];
function tempBarColor(tempF: number): string {
  const lo = TEMP_COLOR_STOPS[0];
  const hi = TEMP_COLOR_STOPS[TEMP_COLOR_STOPS.length - 1];
  const t = Math.max(0, Math.min(1, (tempF - lo.tempF) / (hi.tempF - lo.tempF)));
  const aPct = ((1 - t) * 100).toFixed(1);
  return `color-mix(in srgb, var(${lo.cssVar}) ${aPct}%, var(${hi.cssVar}))`;
}

function formatClock(d: Date): string {
  // 7:24a / 4:38p — compact form for a tight top-right slot. Direction
  // (sunrise vs sunset) is communicated by the adjacent icon; the a/p
  // suffix matches the chart's hour labels at the bottom of the card.
  const h12 = ((d.getHours() + 11) % 12) + 1;
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = d.getHours() < 12 ? 'a' : 'p';
  return `${h12}:${m}${ampm}`;
}

// Open-Meteo phrasings come from a small set of WMO-code labels generated in
// the proxy (e.g. "Mostly Clear", "Light Rain"). Match the dominant token in
// priority order: precipitation > clouds > clear. Unknown phrasings fall
// back to a generic Cloud icon.
function iconFor(shortForecast: string, isDaytime: boolean): Icon {
  const s = shortForecast.toLowerCase();
  if (s.includes('thunder')) return CloudLightningIcon;
  if (s.includes('snow') || s.includes('sleet') || s.includes('flurr')) return CloudSnowIcon;
  if (s.includes('rain') || s.includes('shower') || s.includes('drizzle')) return CloudRainIcon;
  if (s.includes('fog') || s.includes('haze') || s.includes('mist')) return CloudFogIcon;
  if (s.includes('wind')) return WindIcon;
  if (s.includes('hail')) return DropIcon;
  if (s.includes('clear')) {
    if (s.includes('partly') || s.includes('mostly')) {
      return isDaytime ? CloudSunIcon : CloudMoonIcon;
    }
    return isDaytime ? SunIcon : MoonIcon;
  }
  if (s.includes('partly') || s.includes('mostly')) {
    return isDaytime ? CloudSunIcon : CloudMoonIcon;
  }
  if (s.includes('cloud')) return CloudIcon;
  return CloudIcon;
}

// Local YYYY-MM-DD for the given date, using its local timezone components
// (NOT toISOString which is UTC). Used to slice today out of the hourly
// + daily arrays the proxy returns.
function localDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Weather-condition accent for the card surface. Matches the dominant
// token in the same priority order as iconFor() so the icon and glow
// always agree (precipitation > clouds > clear). Night overrides the
// weather mapping with a violet wash regardless of conditions, so the
// card never reads "sunny amber" at midnight. Uses existing tokens so
// the kiosk inherits any future palette tweaks for free.
interface WeatherAccent {
  gradient: string;
  iconColor: string;
  // The soft accent color on its own (e.g. `var(--berg-amber-soft)`) so the
  // card's glow layer can interpolate it via the --weather-glow property.
  glowColor: string;
}

function weatherAccent(shortForecast: string, isDaytime: boolean): WeatherAccent {
  const make = (softVar: string, iconVar: string): WeatherAccent => ({
    gradient: `radial-gradient(circle at top left, var(${softVar}), transparent 80%)`,
    iconColor: `var(${iconVar})`,
    glowColor: `var(${softVar})`,
  });
  if (!isDaytime) return make('--berg-violet-soft', '--berg-violet-light');
  const s = shortForecast.toLowerCase();
  if (s.includes('thunder')) return make('--berg-purple-soft', '--berg-purple-light');
  if (s.includes('snow') || s.includes('sleet') || s.includes('flurr')) {
    return make('--berg-cyan-soft', '--berg-cyan-light');
  }
  if (s.includes('rain') || s.includes('shower') || s.includes('drizzle') || s.includes('hail')) {
    return make('--berg-blue-soft', '--berg-blue');
  }
  if (s.includes('fog') || s.includes('haze') || s.includes('mist')) {
    return make('--berg-neutral-ghost', '--berg-neutral');
  }
  if (s.includes('wind')) return make('--berg-teal-soft', '--berg-teal');
  if (s.includes('clear')) {
    if (s.includes('partly') || s.includes('mostly')) {
      return make('--berg-amber-soft', '--berg-amber');
    }
    return make('--berg-yellow-soft', '--berg-yellow-light');
  }
  if (s.includes('partly') || s.includes('mostly')) {
    return make('--berg-neutral-ghost', '--berg-neutral');
  }
  if (s.includes('cloud')) return make('--berg-neutral-ghost', '--berg-neutral');
  return make('--berg-neutral-ghost', '--berg-neutral');
}

interface WeatherWidgetProps {
  className?: string;
}

export function WeatherWidget({ className }: WeatherWidgetProps = {}) {
  // Parallel slots, one per preset. null = not yet loaded. Each slide
  // renders independently and shows its own loading state, so the
  // carousel works even before every fetch resolves.
  const [dataByPreset, setDataByPreset] = useState<Array<WeatherResponse | null>>(
    () => PRESETS.map(() => null),
  );
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  // Which bottom strip is showing: the next 5 hours or the next 5 days.
  // Lives on the parent so the choice persists as the user swipes between
  // cities. Defaults to hourly — the near-term glance the kiosk is for.
  const [forecastView, setForecastView] = useState<'hourly' | 'daily'>('hourly');
  const swipeStartXRef = useRef<number | null>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const now = useNow(60 * 1000);

  // Clear pending inactivity reset on unmount so a remount of the kiosk
  // doesn't fire a stale setCurrentIndex against a different mount.
  useEffect(() => {
    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, []);

  function scheduleInactivityReset() {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      setCurrentIndex(0);
      setForecastView('hourly');
      inactivityTimerRef.current = null;
    }, INACTIVITY_RESET_MS);
  }

  useEffect(() => {
    let alive = true;
    async function tick() {
      // Prefetch every preset in parallel on each refresh tick. Each result
      // lands in its own slot as soon as it resolves so the visible card
      // never waits on the slowest upstream.
      const results = await Promise.allSettled(
        PRESETS.map(async (p) => {
          const res = await apiFetch(presetQuery(p));
          if (!res.ok) throw new Error(`weather ${res.status}`);
          return (await res.json()) as WeatherResponse;
        }),
      );
      if (!alive) return;
      setDataByPreset((prev) => {
        const next = [...prev];
        for (let i = 0; i < results.length; i++) {
          const r = results[i];
          if (r.status === 'fulfilled') next[i] = r.value;
        }
        return next;
      });
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length === results.length) {
        const first = failed[0] as PromiseRejectedResult;
        setError(first.reason instanceof Error ? first.reason.message : 'fetch failed');
      } else {
        setError(null);
      }
    }
    void tick();
    const stop = pollWithWake(tick, REFRESH_MS);
    return () => {
      alive = false;
      stop();
    };
  }, []);

  // Background condition glow lives on the card (not the swiping strip) so a
  // page change transitions its color in place instead of sliding the wash
  // sideways. The active preset's soft accent feeds the --weather-glow
  // property on the glow layer below; because that property is registered as
  // <color> (see index.css), CSS interpolates it, so the wash morphs from one
  // preset's color to the next. Falls back to transparent before data loads.
  const currentData = dataByPreset[currentIndex];
  const targetGlowColor = currentData
    ? weatherAccent(currentData.current.shortForecast, currentData.current.isDaytime).glowColor
    : 'transparent';

  function pageBy(delta: number) {
    setCurrentIndex((i) => {
      // Clamp at the ends. The strip translateX transforms between
      // indexes; wrapping would require teleporting the strip back to
      // the other end, which breaks the slide animation. Swiping past
      // the first/last preset is a no-op.
      const len = PRESETS.length;
      return Math.max(0, Math.min(len - 1, i + delta));
    });
  }

  return (
    <div
      className={`card-flush relative overflow-hidden touch-none ${className ?? ''}`}
      onPointerDown={(e) => {
        swipeStartXRef.current = e.clientX;
      }}
      onPointerUp={(e) => {
        const startX = swipeStartXRef.current;
        swipeStartXRef.current = null;
        if (startX === null) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
        // swipe left (dx < 0) → next preset; swipe right → previous.
        pageBy(dx < 0 ? 1 : -1);
        // Restart the inactivity timer on every swipe, even ones that
        // clamp at the ends — the user is still actively engaging.
        scheduleInactivityReset();
      }}
      onPointerCancel={() => {
        swipeStartXRef.current = null;
      }}
    >
      {/* Condition glow — lifted out of the slides onto the card itself so a
          page change transitions its color in place rather than sliding the
          wash sideways with the carousel. The gradient shape is constant; only
          the --weather-glow color changes, and CSS interpolates it (the
          property is registered as <color> in index.css). Sits behind the
          strip; the strip's `relative z-10` keeps the content above it. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={
          {
            backgroundImage:
              'radial-gradient(circle at top left, var(--weather-glow), transparent 80%)',
            '--weather-glow': targetGlowColor,
            transition: `--weather-glow ${GLOW_FADE_MS}ms ease-out`,
          } as CSSProperties
        }
      />

      {/* Horizontal strip: one slide per preset, each at 1/N of the
          strip's width = exactly the card's visible width. Translating
          the strip by `-currentIndex × (100 / N)%` snaps the active
          slide into view; the CSS transition makes it slide. */}
      <div
        className="flex relative z-10"
        style={{
          width: `${PRESETS.length * 100}%`,
          transform: `translateX(-${(currentIndex * 100) / PRESETS.length}%)`,
          transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {PRESETS.map((p, i) => {
          // Render-window: only the active slide and its immediate
          // neighbors get the full WeatherCardSlide (charts, SVGs,
          // SunCalc, etc.). The rest are empty flex slots so the strip
          // width + transform math stays identical, but the Pi doesn't
          // burn cycles painting offscreen slides the user can't reach
          // in a single swipe. Neighbors are pre-mounted so the
          // transition has real content to slide in/out.
          const inWindow = Math.abs(i - currentIndex) <= 1;
          return (
            <div
              key={p.name}
              className="shrink-0"
              style={{ width: `${100 / PRESETS.length}%` }}
              aria-hidden={i !== currentIndex || undefined}
            >
              {inWindow && (
                <WeatherCardSlide
                  preset={p}
                  data={dataByPreset[i]}
                  now={now}
                  error={i === currentIndex ? error : null}
                  view={forecastView}
                  onViewChange={setForecastView}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Page dots overlay — pointer-events-none so the swipe gesture
          on the card below isn't intercepted by tapping a dot. */}
      <div className="pointer-events-none absolute bottom-3 left-0 right-0 flex items-center justify-center gap-1.5">
        {PRESETS.map((p, i) => (
          <span
            key={p.name}
            aria-current={i === currentIndex ? 'true' : undefined}
            className={`w-1.5 h-1.5 rounded-full transition-colors ${
              i === currentIndex ? 'bg-fg' : 'bg-fg-tertiary opacity-40'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

// Per-preset slide. Owns its own padding + gradient so each slide looks
// like a complete card while sliding in. Loading + loaded states share
// the same outer wrapper so the slide width stays constant during the
// carousel transform.
function WeatherCardSlide({
  preset,
  data,
  now,
  error,
  view,
  onViewChange,
}: {
  preset: WeatherPreset;
  data: WeatherResponse | null;
  now: Date;
  error: string | null;
  view: 'hourly' | 'daily';
  onViewChange: (next: 'hourly' | 'daily') => void;
}) {
  if (!data) {
    return (
      <div className="p-6 pb-9 text-sm text-fg-secondary">
        {error ?? `Loading ${preset.name}…`}
      </div>
    );
  }

  const { current, hourly, daily, location } = data;
  const accent = weatherAccent(current.shortForecast, current.isDaytime);
  const HeroIcon = iconFor(current.shortForecast, current.isDaytime);

  // Sunrise / sunset for today at the household's lat/lon. SunCalc is a
  // cheap deterministic calc and only re-runs ~once per minute via useClock.
  const sunTimes = SunCalc.getTimes(now, location.lat, location.lon);

  // Today's 48-bar temperature chart at 30-minute resolution. Open-Meteo's
  // hourly array gives one reading per hour; the half-hour bars are
  // linearly interpolated between adjacent hours (temperature changes
  // smoothly enough that this matches reality within a fraction of a
  // degree). Pin to a 48-slot array indexed by half-hour-of-day so the
  // chart always has exactly 48 bars even if the proxy returns a partial
  // day.
  const todayPrefix = localDateString(now);
  const hourTemps = new Map<number, number>();
  for (const h of hourly) {
    if (!h.time.startsWith(todayPrefix)) continue;
    const hour = new Date(h.time).getHours();
    if (hour >= 0 && hour < 24) hourTemps.set(hour, h.temperatureF);
  }
  const SLOT_COUNT = 48;
  interface ChartSlot { temp: number; hour: number; minute: number }
  const todaySlots: Array<ChartSlot | null> = Array.from({ length: SLOT_COUNT }, () => null);
  for (let i = 0; i < SLOT_COUNT; i++) {
    const hour = Math.floor(i / 2);
    const minute = (i % 2) * 30;
    const t0 = hourTemps.get(hour);
    if (t0 === undefined) continue;
    if (minute === 0) {
      todaySlots[i] = { temp: t0, hour, minute };
    } else {
      // Half-hour slot: average this hour with the next (fall back to this
      // hour's reading if the next hour isn't present).
      const t1 = hourTemps.get(hour + 1) ?? t0;
      todaySlots[i] = { temp: (t0 + t1) / 2, hour, minute };
    }
  }
  const validTemps = todaySlots
    .filter((s): s is ChartSlot => s !== null)
    .map((s) => s.temp);
  const dataMin = validTemps.length ? Math.min(...validTemps) : 0;
  const dataMax = validTemps.length ? Math.max(...validTemps) : 1;
  // Y-axis: bottom is anchored at 0°F, but if the day dips below 0 the
  // bottom follows the actual minimum so sub-zero hours stay on the
  // chart. Top auto-scales to the day's high.
  const scaleMin = Math.min(0, dataMin);
  const scaleMax = dataMax;
  const scaleRange = Math.max(1, scaleMax - scaleMin);
  // Current slot index — top of hour or bottom, whichever side of :30 we're on.
  const currentSlot = now.getHours() * 2 + (now.getMinutes() >= 30 ? 1 : 0);

  // 4-day forecast strip below the chart: skip today (already in the hero
  // + chart), take the next 5 days from the daily array.
  const futureDays = daily.filter((d) => d.date > todayPrefix).slice(0, 5);

  // Next-5-hours strip: the hourly array is continuous, so slice from the
  // first reading strictly after the current hour forward — the current
  // hour is skipped since it's already in the hero. Rolling past midnight
  // needs no special handling. Each entry carries its own day/night flag
  // (computed per hour from SunCalc) so evening hours get the moon icon
  // rather than the sun.
  const nextHourStart = new Date(now);
  nextHourStart.setHours(now.getHours() + 1, 0, 0, 0);
  const nextHours = hourly
    .filter((h) => new Date(h.time) >= nextHourStart)
    .slice(0, 5)
    .map((h) => {
      const hourDate = new Date(h.time);
      const t = SunCalc.getTimes(hourDate, location.lat, location.lon);
      const isDay = hourDate >= t.sunrise && hourDate < t.sunset;
      const hour = hourDate.getHours();
      const h12 = ((hour + 11) % 12) + 1;
      return {
        key: h.time,
        label: `${h12}:00`,
        Icon: iconFor(h.shortForecast, isDay),
        temp: h.temperatureF,
      };
    });

  // SVG gradient + filter IDs collide across siblings if reused as-is, so
  // suffix with a per-preset slug. Each slide owns its own pair.
  const idSuffix = preset.name.toLowerCase().replace(/\W+/g, '-');
  const areaGradientId = `weather-area-gradient-${idSuffix}`;
  const nowLineId = `weather-now-line-fade-${idSuffix}`;

  return (
    <div className="relative flex flex-col gap-3 overflow-hidden p-6 pb-9">
      <div className="flex items-center gap-4">
        <HeroIcon
          size={72}
          weight="duotone"
          style={{ color: accent.iconColor, flexShrink: 0 }}
        />
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          <span className="text-6xl tabular tracking-tight leading-none">
            {current.temperatureF}
          </span>
          <div className="text-sm truncate" style={{ color: accent.iconColor }}>
            {current.shortForecast}
          </div>
        </div>
        {/* City name + sunrise / sunset, vertically centered with the hero.
            City name sits above the sun chips as an uppercase tracked stamp
            so it reads as a location label without competing with the
            temperature numeral on the left. */}
        <div className="flex flex-col items-end gap-1.5 text-[0.625rem] uppercase tracking-widest text-fg-tertiary shrink-0">
          <span className="text-fg">{preset.name}</span>
          <div className="flex items-center gap-1">
            <SunHorizonIcon size={14} weight="duotone" />
            <span className="tabular normal-case tracking-normal">
              {formatClock(sunTimes.sunrise)}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <MoonStarsIcon size={14} weight="duotone" />
            <span className="tabular normal-case tracking-normal">
              {formatClock(sunTimes.sunset)}
            </span>
          </div>
        </div>
      </div>

      {/* 48-slot line chart for today at 30-min resolution. The line is
          drawn as 47 short SVG segments between consecutive data points;
          each segment's stroke color is sampled from the average of its
          two temperatures on the indigo→red gradient, so the line shifts
          color with the temperature. Past segments are dimmed to ~45%,
          current + future stay full opacity. A dot on the line marks the
          current 30-min slot. Time labels every 3 hours sit below, plus
          a closing "12a" at the right edge for the next-day boundary. */}
      <div className="pt-3">
        <div className="relative h-11">
          <svg
            viewBox={`0 0 ${SLOT_COUNT * 10} 100`}
            preserveAspectRatio="none"
            className="absolute inset-0 w-full h-full overflow-visible"
            aria-hidden="true"
          >
            <defs>
              {/* Vertical temperature gradient anchored to the chart's
                  scale: indigo at y=100 (the chart's bottom = scaleMin),
                  red at y=0 (the top = scaleMax). The bottom stop is set
                  to stopOpacity=0 and a third stop ~30% up restores full
                  opacity at the correct intermediate color, so the
                  bottom of the area fades cleanly to transparent without
                  distorting the color interpolation. */}
              <linearGradient
                id={areaGradientId}
                x1="0"
                y1="100"
                x2="0"
                y2="0"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor={tempBarColor(scaleMin)} stopOpacity="0" />
                <stop
                  offset="65%"
                  stopColor={tempBarColor(scaleMin + scaleRange * 0.65)}
                  stopOpacity="1"
                />
                <stop offset="100%" stopColor={tempBarColor(scaleMax)} stopOpacity="1" />
              </linearGradient>
              {/* Fade for the "now" vertical line — same y-range as the
                  area gradient so the dashed line fades out at the bottom
                  in lockstep with the area beneath it. */}
              <linearGradient
                id={nowLineId}
                x1="0"
                y1="100"
                x2="0"
                y2="0"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor="var(--berg-text)" stopOpacity="0" />
                <stop offset="65%" stopColor="var(--berg-text)" stopOpacity="1" />
              </linearGradient>
            </defs>
            {/* Area fill: polygon tracing the line on top, down to the
                chart's bottom corners, filled with the vertical color
                gradient. Rendered first so the line segments sit on top. */}
            {(() => {
              const pts: Array<{ x: number; y: number }> = [];
              for (let i = 0; i < todaySlots.length; i++) {
                const s = todaySlots[i];
                if (!s) continue;
                pts.push({
                  x: (i + 0.5) * 10,
                  y: 100 - ((s.temp - scaleMin) / scaleRange) * 100,
                });
              }
              if (pts.length < 2) return null;
              const d =
                `M ${pts[0].x},100 ` +
                pts.map((p) => `L ${p.x},${p.y}`).join(' ') +
                ` L ${pts[pts.length - 1].x},100 Z`;
              return (
                <path
                  d={d}
                  fill={`url(#${areaGradientId})`}
                  opacity={0.4}
                />
              );
            })()}
            {todaySlots.map((s, i) => {
              const next = todaySlots[i + 1];
              if (!s || !next) return null;
              const x1 = (i + 0.5) * 10;
              const x2 = (i + 1.5) * 10;
              const y1 = 100 - ((s.temp - scaleMin) / scaleRange) * 100;
              const y2 = 100 - ((next.temp - scaleMin) / scaleRange) * 100;
              const avgTemp = (s.temp + next.temp) / 2;
              return (
                <line
                  key={`${s.hour}-${s.minute}`}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={tempBarColor(avgTemp)}
                  strokeWidth={2}
                  strokeLinecap="butt"
                  vectorEffect="non-scaling-stroke"
                />
              );
            })}
            {/* "Now" indicator: dashed vertical line dropping from the
                current dot down to the chart's bottom, fading to
                transparent at the bottom in step with the area gradient. */}
            {todaySlots[currentSlot] && (
              <line
                x1={(currentSlot + 0.5) * 10}
                y1={
                  100 -
                  ((todaySlots[currentSlot]!.temp - scaleMin) / scaleRange) * 100
                }
                x2={(currentSlot + 0.5) * 10}
                y2={100}
                stroke={`url(#${nowLineId})`}
                strokeWidth={1}
                strokeDasharray="2 2"
                opacity={0.4}
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>
          {/* Current-slot dot on the line. Positioned in HTML overlay so
              it doesn't get squished by the SVG's non-uniform aspect
              ratio. */}
          {todaySlots[currentSlot] && (
            <div
              className="absolute w-2.5 h-2.5 rounded-full bg-fg -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none"
              style={{
                left: `${((currentSlot + 0.5) / SLOT_COUNT) * 100}%`,
                top: `${100 - ((todaySlots[currentSlot]!.temp - scaleMin) / scaleRange) * 100}%`,
              }}
            />
          )}
        </div>
        {/* Time labels every 3 hours plus a closing "12a" at the right
            edge for the next-day boundary. Positioned absolutely at the
            same x percentages as the SVG line's data points so they align
            without manual flex-gap compensation. */}
        <div className="relative h-3 mt-1">
          {Array.from({ length: SLOT_COUNT }).map((_, i) => {
            if (i % 6 !== 0) return null;
            const hour = Math.floor(i / 2);
            const h12 = ((hour + 11) % 12) + 1;
            const ampm = hour < 12 ? 'a' : 'p';
            return (
              <span
                key={i}
                className="absolute top-0 -translate-x-1/2 text-[0.625rem] text-fg-tertiary tabular whitespace-nowrap leading-none"
                style={{ left: `${((i + 0.5) / SLOT_COUNT) * 100}%` }}
              >
                {h12}{ampm}
              </span>
            );
          })}
          <span className="absolute top-0 right-0 translate-x-1/2 text-[0.625rem] text-fg-tertiary tabular whitespace-nowrap leading-none">
            12a
          </span>
        </div>
      </div>

      {/* Bottom strip: toggles between the next 5 hours and the next 5
          days. The toggle reuses the shared scope-toggle pill classes. A
          tap moves ~0px, below the carousel's swipe threshold, so the
          button's onClick fires without paging the carousel. */}
      <div>
        <div className="flex justify-center mb-2">
          <div className="scope-toggle" role="tablist">
            <button
              role="tab"
              aria-selected={view === 'hourly'}
              onClick={() => onViewChange('hourly')}
              className={`scope-toggle-btn${view === 'hourly' ? ' scope-toggle-btn-active' : ''}`}
            >
              Hourly
            </button>
            <button
              role="tab"
              aria-selected={view === 'daily'}
              onClick={() => onViewChange('daily')}
              className={`scope-toggle-btn${view === 'daily' ? ' scope-toggle-btn-active' : ''}`}
            >
              5-Day
            </button>
          </div>
        </div>

        {view === 'hourly' ? (
          <div className="grid grid-cols-5 gap-2">
            {nextHours.map((h) => (
              <div key={h.key} className="flex flex-col items-center gap-1.5 min-w-0">
                <span className="text-[0.625rem] uppercase tracking-widest text-fg-secondary">
                  {h.label}
                </span>
                <h.Icon size={24} weight="duotone" className="text-fg-secondary" />
                <span className="text-lg font-light tabular">{h.temp}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-5 gap-2">
            {futureDays.map((d) => {
              const DayIcon = iconFor(d.shortForecast, true);
              // Parse date as local noon so the weekday label matches the
              // local calendar regardless of the kiosk's TZ relative to UTC.
              const dateObj = new Date(`${d.date}T12:00:00`);
              const weekday = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
              return (
                <div
                  key={d.date}
                  className="flex flex-col items-center gap-1.5 min-w-0"
                >
                  <span className="text-[0.625rem] uppercase tracking-widest text-fg-secondary">
                    {weekday}
                  </span>
                  <DayIcon size={24} weight="duotone" className="text-fg-secondary" />
                  <span className="text-lg font-light tabular">{d.highF}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}
